/**
 * @fileoverview Account-activation API calls.
 * @description The emailed-code step that turns a REGISTERED account (an invitation was accepted,
 * or a not-yet-activated user signed in — POST /identity/login's nextStep VERIFY_ACTIVATION_MFA)
 * into an ACTIVE one: verifyActivationCode calls POST /identity/activation/mfa/verify and
 * resendActivationCode calls POST /identity/activation/mfa/resend. Activating issues no session:
 * the user signs in normally afterwards (loginRequired).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

export interface VerifyActivationCodeRequest {
  challengeId: string
  otp: string
}

export interface VerifyActivationCodeResponse {
  activated: true
  loginRequired: true
  nextStep: 'LOGIN'
}

export interface ResendActivationCodeRequest {
  challengeId: string
}

/** The replacement activation challenge (ActivationChallengeResponseDto). */
export interface ResendActivationCodeResponse {
  challengeId: string
  channel: 'EMAIL'
  maskedDestination: string
  expiresInSeconds: number
  resendAvailableInSeconds: number
}

/**
 * @description Submits the emailed activation code.
 * @param {VerifyActivationCodeRequest} payload - The challenge id and the entered code.
 * @returns {Promise<VerifyActivationCodeResponse>} Confirmation the account is now active.
 */
export async function verifyActivationCode(
  payload: VerifyActivationCodeRequest,
): Promise<VerifyActivationCodeResponse> {
  const { data } = await httpClient.post<VerifyActivationCodeResponse>(
    '/identity/activation/mfa/verify',
    payload,
  )
  return data
}

/**
 * @description Requests a fresh activation code, invalidating the previous one.
 * @param {ResendActivationCodeRequest} payload - The pending challenge's id.
 * @returns {Promise<ResendActivationCodeResponse>} The replacement challenge.
 */
export async function resendActivationCode(
  payload: ResendActivationCodeRequest,
): Promise<ResendActivationCodeResponse> {
  const { data } = await httpClient.post<ResendActivationCodeResponse>(
    '/identity/activation/mfa/resend',
    payload,
  )
  return data
}
